import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { NativeSelect } from '@/components/ui/native-select'

/** The part of a parsed file the mapping table needs. */
export interface MappableFile<F extends string> {
  headers: string[]
  rows: string[][]
  suggested_mapping: (F | null)[]
}

interface ColumnMappingProps<F extends string> {
  parsed: MappableFile<F>
  mapping: (F | null)[]
  fields: readonly { key: F; label: string }[]
  /** Why the mapping cannot be used (Dutch); "Verder" waits while there are any. */
  problems: readonly string[]
  onChange: (mapping: (F | null)[]) => void
  onBack: () => void
  onNext: () => void
  busy: boolean
  nextLabel?: string
}

function examples(rows: string[][], column: number): string {
  return rows
    .map((row) => row[column] ?? '')
    .filter(Boolean)
    .slice(0, 3)
    .join(' · ')
}

/**
 * Kolommen koppelen (shared by the lead import, spec-60 B2, and the CRM import,
 * spec-63 E6): which column feeds which field; unmapped columns are not read.
 */
export function ColumnMapping<F extends string>({
  parsed,
  mapping,
  fields,
  problems,
  onChange,
  onBack,
  onNext,
  busy,
  nextLabel = 'Verder: controleren',
}: ColumnMappingProps<F>) {
  return (
    <section className="vonk-card flex flex-col gap-3 p-4" aria-labelledby="import-columns">
      <h2 id="import-columns" className="text-base font-semibold text-foreground">
        Kolommen koppelen
      </h2>
      <p className="text-sm text-muted-foreground">
        Kies per kolom het veld. Kolommen die je niet koppelt, worden niet ingelezen.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold text-muted-foreground">
            <th scope="col" className="py-2 pr-3">
              Kolom in bestand
            </th>
            <th scope="col" className="py-2 pr-3">
              Voorbeeld
            </th>
            <th scope="col" className="py-2">
              Koppelen aan
            </th>
          </tr>
        </thead>
        <tbody>
          {parsed.headers.map((header, column) => {
            const field = mapping[column] ?? null
            const recognised = field !== null && field === parsed.suggested_mapping[column]
            const sample = examples(parsed.rows, column)
            return (
              <tr key={`${column}-${header}`} className="border-t border-border">
                <td className="py-2 pr-3 font-semibold text-foreground">{header}</td>
                <td className="max-w-96 truncate py-2 pr-3 text-muted-foreground" title={sample}>
                  {sample || '—'}
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <NativeSelect
                      aria-label={`Koppeling voor ${header}`}
                      value={field ?? ''}
                      onChange={(event) => {
                        const next = [...mapping]
                        next[column] = (event.target.value || null) as F | null
                        onChange(next)
                      }}
                    >
                      <option value="">— niet importeren</option>
                      {fields.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </NativeSelect>
                    {recognised ? (
                      <span className="inline-flex items-center gap-1 text-xs text-success">
                        <Check className="size-3.5" aria-hidden />
                        herkend
                      </span>
                    ) : null}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {problems.length > 0 ? (
        <ul role="alert" className="flex flex-col gap-1 text-sm text-danger">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex justify-between gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Terug
        </Button>
        <Button type="button" onClick={onNext} disabled={problems.length > 0 || busy}>
          {busy ? 'Bezig met controleren…' : nextLabel}
        </Button>
      </div>
    </section>
  )
}
